"use client"
import { useState, useEffect } from "react"
import axios from "axios" // axios ইম্পোর্ট করা হয়েছে
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Typography,
  Autocomplete,
  Box,
} from "@mui/material"
import { Close as CloseIcon } from "@mui/icons-material"
import BaseURL from "../../api/AppURL";

// API বেস URL
const API_BASE_URL = `${BaseURL}`

// Axios ইনস্ট্যান্স তৈরি করা হয়েছে
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
})

const AddSupplierGroupModal = ({ open, onClose, onSaveSuccess }) => {
  const [groups, setGroups] = useState([])
  const [loadingGroups, setLoadingGroups] = useState(true)
  const [selectedGroupObject, setSelectedGroupObject] = useState(null)
  const [supplierGroupName, setSupplierGroupName] = useState("")
  const [loadingSave, setLoadingSave] = useState(false)
  const [error, setError] = useState(null)
  const [isEditMode, setIsEditMode] = useState(false)
  const [currentGroupId, setCurrentGroupId] = useState(null)

  useEffect(() => {
    if (open) {
      // Modal খোলার সময় স্টেট রিসেট করুন
      setGroups([])
      setSelectedGroupObject(null)
      setSupplierGroupName("")
      setError(null)
      setIsEditMode(false) // মোডাল খুললে ডিফল্টভাবে অ্যাড মোড
      setCurrentGroupId(null)
      fetchGroups()
    }
  }, [open])

  const fetchGroups = async () => {
    setLoadingGroups(true)
    setError(null)
    try {
      const token = localStorage.getItem("auth_token")
      // api ইনস্ট্যান্স ব্যবহার করা হয়েছে
      const response = await api.get("/all/supplier", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      if (response.data && Array.isArray(response.data.groups)) {
        setGroups(response.data.groups)
      } else {
        setGroups([])
        setError("No groups found or invalid response format.")
      }
    } catch (err) {
      console.error("Error fetching groups:", err)
      setError("Failed to load groups. Please check your network or API.")
    } finally {
      setLoadingGroups(false)
    }
  }

  const fetchGroupDataForEdit = async (id) => {
    setLoadingSave(true) // লোডিং স্টেট সেট করুন
    setError(null)
    try {
      const token = localStorage.getItem("auth_token")
      // api ইনস্ট্যান্স ব্যবহার করা হয়েছে
      const response = await api.get(`/edit/sgroup/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      if (response.data && response.data.data) {
        setSupplierGroupName(response.data.data.name || "")
        setIsEditMode(true) // ডেটা লোড হওয়ার পর এডিট মোড সেট করুন
        setCurrentGroupId(id)
      } else {
        setError("Group data not found for editing.")
      }
    } catch (err) {
      console.error("Error fetching group data for edit:", err)
      if (axios.isAxiosError(err)) {
        if (err.response) {
          console.error("Error response data:", err.response.data)
          console.error("Error response status:", err.response.status)
          console.error("Error response headers:", err.response.headers)
        } else if (err.request) {
          console.error("Error request:", err.request)
        } else {
          console.error("Error message:", err.message)
        }
      } else {
        console.error("Non-Axios error:", err)
      }
      setError("Failed to load group data for editing. Please try again.")
    } finally {
      setLoadingSave(false) // লোডিং স্টেট রিসেট করুন
    }
  }

  // এই ফাংশনটি "Edit" এবং "Don't Edit" বাটন টগল করার জন্য ব্যবহৃত হবে
  const handleToggleEditMode = () => {
    if (isEditMode) {
      // যদি বর্তমানে "Don't Edit" মোডে থাকে, তাহলে রিসেট করুন
      setSupplierGroupName("")
      setSelectedGroupObject(null)
      setIsEditMode(false)
      setCurrentGroupId(null)
      setError(null)
    } else {
      // যদি বর্তমানে "Edit" মোডে থাকে এবং একটি গ্রুপ নির্বাচিত থাকে, তাহলে ডেটা ফেচ করুন
      if (selectedGroupObject) {
        fetchGroupDataForEdit(selectedGroupObject.id)
      } else {
        setError("Please select a group to edit.")
      }
    }
  }

  const handleSaveOrUpdate = async () => {
    setLoadingSave(true)
    setError(null)
    const token = localStorage.getItem("auth_token")
    if (!token) {
      setError("Authentication token not found. Please log in again.")
      setLoadingSave(false)
      return
    }

    // Basic client-side validation
    if (!supplierGroupName.trim()) {
      setError("Validation Error:\nSupplier Group Name is required.")
      setLoadingSave(false)
      return
    }

    try {
      let response
      if (isEditMode && currentGroupId) {
        // Update API call
        response = await api.put(
          `/update/sgroup/${currentGroupId}`,
          {
            sgroup_name: supplierGroupName,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )
      } else {
        // Save API call
        response = await api.post(
          "/store/sgroup",
          {
            sgroup_name: supplierGroupName,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )
      }
      console.log("API Response on Save/Update:", response.data)
      if (response.data.success === true) {
        onSaveSuccess(response.data.message || "Operation successful!")
        onClose()
      } else {
        setError(response.data.message || "Failed to perform operation.")
      }
    } catch (err) {
      console.error("Error saving/updating supplier group:", err)
      if (axios.isAxiosError(err)) {
        if (err.response) {
          console.error("Error response data:", err.response.data)
          console.error("Error response status:", err.response.status)
          console.error("Error response headers:", err.response.headers)
        } else if (err.request) {
          console.error("Error request:", err.request)
        } else {
          console.error("Error message:", err.message)
        }
      } else {
        console.error("Non-Axios error:", err)
      }

      if (err.response && err.response.data && err.response.data.errors) {
        const validationErrors = Object.values(err.response.data.errors).flat().join("\n")
        setError(`Validation Error:\n${validationErrors}`)
      } else if (err.response && err.response.data && err.response.data.message) {
        setError(err.response.data.message)
      } else {
        setError(
          "An unexpected error occurred while saving/updating the supplier group. Please check your network connection, API server status, or CORS configuration.",
        )
      }
    } finally {
      setLoadingSave(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          {isEditMode ? "Edit Supplier Group" : "Add Supplier Group"}
        </Typography>
        <IconButton aria-label="close" onClick={onClose} sx={{ color: (theme) => theme.palette.grey[500] }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2, whiteSpace: "pre-wrap" }}>
            {error}
          </Alert>
        )}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: "medium" }}>
            Select Group
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Autocomplete
              id="select-supplier-group-autocomplete"
              options={groups}
              getOptionLabel={(option) => option.name || ""}
              value={selectedGroupObject}
              onChange={(event, newValue) => {
                setSelectedGroupObject(newValue)
                // যখন Autocomplete থেকে একটি গ্রুপ নির্বাচিত হয়, তখন সরাসরি এডিট মোড সেট করবেন না।
                // "Edit" বাটনে ক্লিক করার পর এডিট মোড সেট হবে।
                // যদি নির্বাচন বাতিল করা হয় (newValue null হয়), তাহলে সব রিসেট করুন।
                if (!newValue) {
                  setSupplierGroupName("")
                  setCurrentGroupId(null)
                  setIsEditMode(false)
                  setError(null)
                }
              }}
              loading={loadingGroups}
              disabled={loadingGroups || loadingSave}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Group"
                  variant="outlined"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingGroups ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              noOptionsText={loadingGroups ? "Loading groups..." : "No groups available"}
              sx={{ flexGrow: 1 }}
            />
            <Button
              variant="contained"
              color={isEditMode ? "error" : "primary"} // এডিট মোডে থাকলে লাল রঙ
              onClick={handleToggleEditMode} // নতুন হ্যান্ডলার
              disabled={(!selectedGroupObject && !isEditMode) || loadingSave} // নির্বাচিত গ্রুপ না থাকলে বা এডিট মোডে না থাকলে ডিসেবল
            >
              {isEditMode ? "Don't Edit" : "Edit"} {/* এডিট মোডে থাকলে "Don't Edit" দেখাবে */}
            </Button>
          </Box>
        </Box>
        <TextField
          label="Supplier Group Name"
          placeholder="Supplier Group Name"
          fullWidth
          margin="normal"
          value={supplierGroupName}
          onChange={(e) => setSupplierGroupName(e.target.value)}
          variant="outlined"
          size="small"
          disabled={loadingSave}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", p: 2 }}>
        <Button variant="contained" onClick={handleSaveOrUpdate} disabled={loadingSave} sx={{ minWidth: 100 }}>
          {loadingSave ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : isEditMode ? "Update" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddSupplierGroupModal
